<!-- Full-window YouTube or Vimeo player; VideoGallery shows it in browser fullscreen when it can.
  Pause and play stay inside the player; this shell only handles leaving: Escape, a click on the
  dark surround, or Close (shown while paused, and briefly after pointer activity). -->

<template>
  <div
    ref="shell"
    class="shell"
    tabindex="-1"
    @click.self="close"
    @mousemove="onPointerActivity"
  >
    <div class="screen" @mouseenter="onPointerActivity" @mouseleave="refocusShell">
      <vue-vimeo-player
        v-if="source.vimeoId"
        :video-id="source.vimeoId"
        :options="{responsive: true, autoplay: true, playsinline: isFullscreen}"
        @ready="onVimeoReady"
        @play="isPaused = false"
        @pause="isPaused = true"
        @ended="onVimeoEnded"
      />
      <div v-else ref="youtubeTarget" />
    </div>
    <button
      v-if="isCloseVisible"
      type="button"
      class="close"
      aria-label="Close"
      @click="close"
      @mouseenter="isHoveringClose = true"
      @mouseleave="isHoveringClose = false"
    >
      <img :src="closeIcon" alt="" />
    </button>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {vueVimeoPlayer} from "vue-vimeo-player";
import closeIcon from "@/assets/img/close.svg";
import {fetchYouTubeApi} from "@/composables/useYouTubeApi";
import {getVideoSource} from "@/composables/useVideoSource";
const props = defineProps({
  video: {type: Object, required: true},
  isFullscreen: {type: Boolean, default: false}
});
// ended: the video played to its end (a gallery may loop to the next)
const emit = defineEmits(["hide", "ended"]);
// wait for autoplay to start before reading the initial state, so Close doesn't flash on open
const SETTLE_DELAY = 1000;
// roughly how long YouTube and Vimeo keep their controls up after the pointer stops
const CONTROLS_HIDE_DELAY = 3000;
const isYouTubeStopped = (state) => {
  const {PLAYING, BUFFERING} = window.YT.PlayerState;
  return state !== PLAYING && state !== BUFFERING;
};
const source = getVideoSource(props.video.url);
const shell = ref(null);
const youtubeTarget = ref(null);
const isPaused = ref(false);
const isPointerActive = ref(false);
const isHoveringClose = ref(false);
const isCloseVisible = computed(
  () => isPaused.value || isPointerActive.value || isHoveringClose.value
);
let youtubePlayer = null;
let settleTimer = null;
let hideTimer = null;
let previousOverflow = "";
const close = () => emit("hide");
// clicking into the player moves keyboard focus there, where Escape never reaches this page
const refocusShell = () => shell.value?.focus({preventScroll: true});
// moves inside the cross-origin player are invisible here; only entering it, or the surround
const onPointerActivity = () => {
  isPointerActive.value = true;
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    isPointerActive.value = false;
  }, CONTROLS_HIDE_DELAY);
};
const onKeydown = (event) => {
  if (event.key !== "Escape" || document.fullscreenElement) return;
  event.preventDefault();
  close();
};
const onVimeoReady = (player) => {
  settleTimer = setTimeout(async () => {
    isPaused.value = await player.getPaused();
  }, SETTLE_DELAY);
};
// return to the thumbnail instead of Vimeo's "More from VIZBI" end screen
const onVimeoEnded = (data, player) => {
  isPaused.value = true;
  player.unload();
  emit("ended");
};
const onYouTubeReady = ({target}) => {
  settleTimer = setTimeout(() => {
    isPaused.value = isYouTubeStopped(target.getPlayerState());
  }, SETTLE_DELAY);
};
const onYouTubeStateChange = ({data}) => {
  if (data === window.YT.PlayerState.UNSTARTED) return;
  isPaused.value = isYouTubeStopped(data);
  if (data === window.YT.PlayerState.ENDED) emit("ended");
};
const createYouTubePlayer = async () => {
  const YT = await fetchYouTubeApi();
  if (!youtubeTarget.value) return;
  youtubePlayer = new YT.Player(youtubeTarget.value, {
    videoId: source.youtubeId,
    // in our fullscreen, YouTube's own button would only confuse (it can't tell it's fullscreen);
    // without it (e.g. iPhone), playing out of line lets iOS use its native fullscreen player
    playerVars: {
      autoplay: 1,
      rel: 0,
      playsinline: props.isFullscreen ? 1 : 0,
      fs: props.isFullscreen ? 0 : 1
    },
    events: {onReady: onYouTubeReady, onStateChange: onYouTubeStateChange}
  });
};
onMounted(() => {
  previousOverflow = document.documentElement.style.overflow;
  document.documentElement.style.overflow = "hidden";
  document.addEventListener("keydown", onKeydown, true);
  shell.value.focus();
  if (source.youtubeId) createYouTubePlayer();
});
onUnmounted(() => {
  document.documentElement.style.overflow = previousOverflow;
  document.removeEventListener("keydown", onKeydown, true);
  clearTimeout(settleTimer);
  clearTimeout(hideTimer);
  youtubePlayer?.destroy();
});
</script>

<style scoped>
.shell {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.9);
  outline: none;
}
.screen {
  width: min(100vw, calc(100dvh * 16 / 9));
  aspect-ratio: 16 / 9;
}
.screen :deep(iframe) {
  width: 100%;
  height: 100%;
  border: 0;
}
.close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}
.close img {
  width: 100%;
  height: 100%;
}
</style>
